import { createApiClient } from '@/services/api';
import { useAuth } from '@clerk/clerk-react';
import type { WorkoutAttribution } from '@ergshoot/shared';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Hook that provides individuals API methods with proper Clerk token handling.
 * Uses refs and callbacks to ensure stable function references across renders.
 */
export function useIndividualsApi() {
  const { getToken } = useAuth();

  // Store getToken in a ref to avoid dependency changes
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Create a stable token getter that always uses the latest getToken
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const individualsApi = useMemo(() => {
    const client = createApiClient(stableGetToken);

    return {
      getDashboard: () =>
        client.get<{
          user: {
            id: string;
            username: string;
            name: string;
            email: string;
            subscription_status: string;
            member_since: string;
          };
          recent_workouts: Array<{
            id: string;
            title: string;
            workout_date: string;
            track_name: string | null;
          }>;
          total_scores: number;
          global_rank: number;
          total_participants: number;
          recent_scores: Array<{
            id: string;
            result: string;
            rx: boolean;
            notes: string | null;
            created_at: string;
            workout_title: string;
          }>;
        }>('/individuals/dashboard'),

      getProfile: () =>
        client.get<{
          user: {
            id: string;
            email: string;
            username: string;
            name: string;
            country_code: string | null;
            gender: string | null;
            date_of_birth: string | null;
            height_cm: number | null;
            weight_kg: number | null;
            subscription_status: string;
            member_since: string;
          };
        }>('/individuals/profile'),

      updateProfile: (data: Record<string, string | number | undefined>) =>
        client.patch<{
          user: {
            id: string;
            email: string;
            username: string;
            name: string;
            country_code: string | null;
            gender: string | null;
            date_of_birth: string | null;
            height_cm: number | null;
            weight_kg: number | null;
            subscription_status: string;
            member_since: string;
          };
          message: string;
        }>('/individuals/profile', data),

      getBillingPortal: (returnUrl?: string) =>
        client.post<{
          url: string;
          user: {
            id: string;
            name: string;
            subscription_status: string | null;
          };
        }>('/individuals/billing/portal', { return_url: returnUrl }),

      createCheckoutSession: () =>
        client.post<{
          checkout_url: string;
          session_id: string;
          user: {
            id: string;
            name: string;
          };
        }>('/individuals/billing/checkout', {}),

      // Workouts API
      getWorkouts: (params?: { date?: string }) => {
        const searchParams = new URLSearchParams();
        if (params?.date) searchParams.set('date', params.date);
        const queryString = searchParams.toString();
        return client.get<{
          workouts: Array<{
            id: string;
            name: string;
            description: string;
            workout_date: string;
            scoring_type: string;
            track: { id: string; name: string } | null;
            attribution?: WorkoutAttribution | null;
            created_at?: string | null;
            created_by_username?: string | null;
            my_score: {
              value: number;
              unit: string;
              notes: string | null;
              logged_at: string;
            } | null;
          }>;
          date: string;
        }>(`/individuals/workouts${queryString ? `?${queryString}` : ''}`);
      },

      getWorkout: (workoutId: string) =>
        client.get<{
          workout: {
            id: string;
            name: string;
            description: string;
            workout_date: string;
            scoring_type: string;
            track: { id: string; name: string } | null;
            attribution?: WorkoutAttribution | null;
            created_at?: string | null;
            created_by_username?: string | null;
            my_score: {
              value: number;
              unit: string;
              notes: string | null;
              logged_at: string;
            } | null;
          };
        }>(`/individuals/workouts/${workoutId}`),

      // Scores API
      logScore: (
        data: {
          workout_id: string;
          score_value: number;
          score_unit: string;
          notes?: string;
        },
        idempotencyKey?: string
      ) =>
        client.post<{
          score: {
            id: string;
            workout_id: string;
            result: string;
            notes: string | null;
            updated: boolean;
          };
          message: string;
        }>('/individuals/scores', data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      // Leaderboard API
      getLeaderboard: (
        workoutId: string,
        params?: {
          limit?: number;
          country?: string;
          gender?: string;
          age_group?: string;
          search?: string;
        }
      ) => {
        const searchParams = new URLSearchParams();
        if (params?.limit) searchParams.set('limit', params.limit.toString());
        if (params?.country) searchParams.set('country', params.country);
        if (params?.gender) searchParams.set('gender', params.gender);
        if (params?.age_group) searchParams.set('age_group', params.age_group);
        if (params?.search) searchParams.set('search', params.search);
        const queryString = searchParams.toString();
        return client.get<{
          workout: {
            id: string;
            name: string;
            scoring_type: string;
          };
          leaderboard: Array<{
            rank: number;
            user_id: string;
            user_name: string;
            user_country: string | null;
            affiliate_name: string | null;
            score_value: number;
            score_unit: string;
            logged_at: string;
          }>;
          filters: Record<string, string>;
        }>(`/individuals/leaderboard/${workoutId}${queryString ? `?${queryString}` : ''}`);
      },

      getProgress: () =>
        client.get<{
          summary: {
            total_scores: number;
            unique_workouts: number;
            current_streak: number;
            total_days: number;
          };
          weekly_trend: Array<{ week: string; count: number }>;
          recent_scores: Array<{
            id: string;
            result: string;
            rx: boolean;
            notes: string | null;
            created_at: string;
            workout_title: string;
            scoring_type: string;
            track_name: string | null;
          }>;
        }>('/individuals/progress'),

      getPRs: () =>
        client.get<{
          prs: Array<{
            workout_id: string;
            workout_title: string;
            track_name: string | null;
            scoring_type: string;
            best_result: string;
            best_value: number;
            achieved_at: string;
            attempts: number;
          }>;
          total_prs: number;
        }>('/individuals/prs'),
    };
  }, [stableGetToken]);

  return individualsApi;
}
